import ModalComponent from "../../../../general-ui/modal/modal";
import TabHeader from "../../../../general-ui/tab-header/tab-header";
import classNames from "classnames";
import './broadcast-card.scss';
import { SwitchNew } from "../../../../general-ui/switch/switch-new";
import { UploadButton } from './broadcast-card';
import { Session } from "../../../../../types/working-model";
import { useState, useEffect } from "react";
import { useDispatch, batch } from "react-redux";
import { UpdateAllSessionsOverlayChat } from "../../../../../connection/sessions";
import { updateAllEventSessionsOverlaChat, updateWorkingEventSession } from "../../../../../store/actions/admin/create-event";
import { updateSession, updateWorkingSession } from "../../../../../store/actions/admin/create-event/session";
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import usePrevious from "../../../../../utils/use-previous";
import chatOverlay from '../../../../../images/ui-assets/chat-overlay.svg';
import chatSidebar from '../../../../../images/ui-assets/chat-sidebar.svg';

enum ETabs {
	Layout = 'Layout',
	Session = 'Session'
}

interface BroadcastOptionsModalProps {
	isOpen: boolean;
	onClose: () => void;
}

const BroadcastOptionsModal: React.FC<BroadcastOptionsModalProps> = ({ isOpen, onClose }) => {
	const {
		token,
		workingSession,
		workingEvent,
	} = useTypedSelector(state => ({
		token: state.AuthReducer.token,
		workingSession: state.CreateSessionReducer.workingSession,
		workingEvent: state.CreateEventReducer.workingEvent,
	}));

	const [open, setOpen] = useState(false);
	const [activeTab, setActiveTab] = useState(ETabs.Layout);
	const [isOverlay, setIsOverlay] = useState(!!workingSession?.layout?.overlayChat);
	const [applyAllOverlay, setApplyAllOverlay] = useState(false);
	const [preBroadcastImageFile, setPreBroadcastImageFile] = useState<string | undefined>(workingSession?.pre_broadcast_thumbnail || undefined);
	const [postBroadcastImageFile, setPostBroadcastImageFile] = useState<string | undefined>(workingSession?.post_broadcast_thumbnail || undefined);
	const [uploading, setUploading] = useState(false);
	const [shouldUseDifferentThumbnails, setShouldUseDifferentThumbnails] = useState(!!workingSession?.use_different_thumbnails);

	const dispatch = useDispatch();

	// on open, make sure to reset isOverlay
	const previous = usePrevious(open);
	useEffect(() => {
		if (!previous && open) {
			setIsOverlay(!!workingSession?.layout?.overlayChat);
		}
	}, [open, previous, workingSession?.layout?.overlayChat]);

	useEffect(() => setPreBroadcastImageFile(workingSession?.pre_broadcast_thumbnail || undefined), [workingSession?.pre_broadcast_thumbnail]);
	useEffect(() => setPostBroadcastImageFile(workingSession?.post_broadcast_thumbnail || undefined), [workingSession?.post_broadcast_thumbnail]);

	const handleClose = () => {
		setOpen(false);
		// handle jumping state
		setTimeout(() => {
			setPreBroadcastImageFile(workingSession?.pre_broadcast_thumbnail || undefined);
			setPostBroadcastImageFile(workingSession?.post_broadcast_thumbnail || undefined);
			setActiveTab(ETabs.Layout);
		}, 500);
		onClose();
	};

	const handleSave = async () => {
		try {
			if (!token || !workingSession || !workingEvent) return;
			if (activeTab === ETabs.Layout) {
				const updatedSession: Session = {
					...workingSession,
					layout: {
						...workingSession.layout,
						overlayChat: isOverlay,
					}
				};
				batch(() => {
					dispatch(updateSession(updatedSession, token));
					dispatch(updateWorkingSession(updatedSession));
				});
				if (applyAllOverlay) {
					// update all sessions in db and in redux working event
					await UpdateAllSessionsOverlayChat(token, workingEvent.uuid, isOverlay);
					dispatch(updateAllEventSessionsOverlaChat(isOverlay));
				} else {
					dispatch(updateWorkingEventSession(updatedSession));
				}
			}

			if (activeTab === ETabs.Session) {
				const updatedSession: Session = {
					...workingSession,
					use_different_thumbnails: shouldUseDifferentThumbnails,
					post_broadcast_thumbnail: postBroadcastImageFile,
					pre_broadcast_thumbnail: preBroadcastImageFile,
				};
				const workingEventSession: Session | undefined = workingEvent?.sessions.find(session => session.uuid === workingSession.uuid);

				// update database and redux
				batch(() => {
					dispatch(updateSession(updatedSession, token));
					dispatch(updateWorkingSession(updatedSession));
					workingEventSession && dispatch(updateWorkingEventSession(updatedSession));
				});
			}

		} catch (e) {
			console.error(e?.response || e);
		}
		setTimeout(() => setActiveTab(ETabs.Layout), 500);
		onClose();
	};

	const handleAllOverlaySwitch = (name: string | undefined, isOn: boolean) => {
		setApplyAllOverlay(isOn);
	};

	const handleSidePanelClick = () => {
		setIsOverlay(false);
		setApplyAllOverlay(false);
	};

	const handleDeleteImage = (type: 'pre' | 'post') => (setUploadedUri: React.Dispatch<React.SetStateAction<string | undefined>>) => {
		if (!workingSession || !token) return;
		if (type === 'pre') setPreBroadcastImageFile(undefined);
		if (type === 'post') setPostBroadcastImageFile(undefined);
		setUploadedUri(undefined);
	};

	useEffect(() => {
		setOpen(isOpen);
	}, [isOpen]);

	return (
		<ModalComponent
			open={open}
			closeable={false}
			title="Broadcast"
			onRequestClose={handleClose}
			trapFocus={false}
			cancellable
			footer={
				<div>
					<button onClick={handleClose}>Cancel</button>
					<button disabled={uploading} className="primary" onClick={handleSave}>Save</button>
				</div>
			}
		>
			<div className="broadcast-card-modal">
				<div className="broadcast-card-modal-tabs">
					<TabHeader
						tabs={[ETabs.Layout, ETabs.Session]}
						onClick={value => {
							setActiveTab(value);
						}}
					/>
				</div>
				<div className="broadcast-card-modal-content">
					{activeTab === ETabs.Layout ? (
						<>
							<div className="session-layout-type">
								<div className="session-layout-row">
									<div className={classNames("session-layout-option", { active: isOverlay })}>
										<button onClick={() => setIsOverlay(true)} className="clear no-style inherit-dimensions">
											<img src={chatOverlay} alt="" />
											<label>Chat Overlay</label>
										</button>
									</div>
									<div className={classNames("session-layout-option", { active: !isOverlay })}>
										<button onClick={handleSidePanelClick} className="clear no-style inherit-dimensions">
											<img src={chatSidebar} alt="" />
											<label>Chat in side panel</label>
										</button>
									</div>
								</div>
							</div>
							<div>
								<SwitchNew
									content="Apply to all sessions"
									onChange={handleAllOverlaySwitch}
								/>
								{applyAllOverlay}
							</div>
						</>
					) : (
						<div className="broadcast-thumbnail-container">
							<h6>Add image thumbnail</h6>
							<p className="broadcast-thumbnail-paragraph">Image attendee will see before a video starts and after it ends.</p>
							<UploadButton
								initialImage={preBroadcastImageFile}
								onStartUpload={() => {
									setUploading(true);
								}}
								uploadedImage={url => {
									setPreBroadcastImageFile(url);
									setUploading(false);
								}}
								handleDeleteImage={handleDeleteImage('pre')}
							/>

							<div className="use-different-thumbs-container">
								<SwitchNew
									checked={shouldUseDifferentThumbnails}
									content="Use different image after session ends"
									onChange={() => setShouldUseDifferentThumbnails(prev => !prev)}
								/>
							</div>

							{shouldUseDifferentThumbnails ? (
								<UploadButton
									initialImage={postBroadcastImageFile}
									onStartUpload={() => {
										setUploading(true);
									}}
									uploadedImage={url => {
										setPostBroadcastImageFile(url);
										setUploading(false);
									}}
									handleDeleteImage={handleDeleteImage('post')}
								/>
							) : null}
						</div>
					)}
				</div>
			</div>
		</ModalComponent>
	);
};

export default BroadcastOptionsModal;